import { cj1 } from '@/lib/content-lifecycle/contracts';
import { createVoiceTiming } from '@/lib/voice-timing';
import type { ParsedPrayer, Prayer } from '@/types/prayer';
import i18n from '@/i18n';
import { getSupabase } from '@/lib/supabase';
import { enhancedVoiceAllowed } from '@/lib/core-cutover';
import {
  authorizePrayerOutbound,
  captureOutboundOwnerBinding,
  outboundOwnerStillCurrent,
  ownerBoundJsonHeaders,
  prayerOutboundExpectation,
  themeOutboundExpectation,
  type PrayerThemeInput,
  type OutboundOwnerBinding,
  type PrayerOutboundExpectation,
} from '@/lib/outbound-prayer-authorization';

const PROXY_URL = '/api/grok';
const SUMMARY_URL = '/api/grok-summary';
// V.A.2: cut over from the legacy Edge endpoint (/api/grok-transcribe)
// to the Node-runtime v2 endpoint that takes a Supabase Storage path.
// The Edge endpoint's 4.5 MB request body cap blocked 7-min Safari iOS
// prayers; v2 reads bytes server-side from Storage and forwards to xAI
// without round-tripping through the function's body. See HANDOFF
// V.A arc + CLAUDE.md rule #14 for the dual-path architecture context.
const TRANSCRIBE_URL = '/api/grok-transcribe-v2';
const THEMES_URL = '/api/grok-themes';

export const PARSE_PRAYER_TEXT_LIMIT = 4000;
export const GARDEN_SUMMARY_THREAD_LIMIT = 6000;

export function preparePrayerEnrichmentText(rawText: string): string {
  return rawText.slice(0, PARSE_PRAYER_TEXT_LIMIT);
}

export function buildGardenSummaryThread(prayer: Prayer): string {
  let thread = preparePrayerEnrichmentText(prayer.rawText).trim();
  for (const entry of prayer.progressHistory ?? []) {
    const prefix = thread ? '\nUpdate: ' : 'Update: ';
    const remaining = GARDEN_SUMMARY_THREAD_LIMIT - thread.length;
    if (remaining <= prefix.length) break;
    thread += prefix + entry.note.slice(0, remaining - prefix.length);
    if (thread.length >= GARDEN_SUMMARY_THREAD_LIMIT) break;
  }
  return thread;
}

function currentLang(): string {
  return (i18n.language ?? 'en').slice(0, 2);
}

export class ParseError extends Error {
  status?: number;
  /** Stable server protocol code plus CAS tip, when the route supplied one. */
  serverCode?: string;
  registryRevision?: string;
  registryState?: string;
  // V.B.1: when true, the queue processor short-circuits to terminal
  // failure instead of burning the 6-attempt budget. Set for status
  // codes that won't be helped by retry (413 payload-too-large, other
  // 4xx malformed). Read by audio-queue.ts processOne so the same durable
  // payload is not retried pointlessly. See classifyTranscribeStatus in
  // transcribeAudio for the full retry table.
  terminal?: boolean;
  /**
   * Rule #21: true only when transcribeAudio failed for a temporary reason of
   * the transcription service itself, as classifyTranscribeStatus defines it:
   * a non-terminal status that is not a registry conflict, or the request's
   * own timeout (the 504/408 class). The foreground voice hook starts its
   * one-minute service fallback only for this
   * (isTemporaryTranscriptionServiceFailure), so the next NEW recording may
   * use browser-native speech recognition. Terminal outcomes, conflicts,
   * ownership changes, local storage failures and retired captures never set
   * it.
   */
  temporaryServiceFailure?: boolean;
  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ParseError';
    this.status = status;
  }
}

async function authorizePrayerRequest(
  binding: OutboundOwnerBinding | null,
  expectations: readonly PrayerOutboundExpectation[],
): Promise<Record<string, string>> {
  if (!binding) throw new ParseError('Prayer owner is not available.');
  const headers = await ownerBoundJsonHeaders(binding);
  if (
    !headers ||
    !(await authorizePrayerOutbound(binding, expectations)) ||
    !outboundOwnerStillCurrent(binding)
  ) {
    throw new ParseError('Prayer changed before the request was sent.');
  }
  return headers;
}

export type ParsePrayerAuthorization = {
  target: PrayerOutboundExpectation;
  context: readonly PrayerOutboundExpectation[];
};

export async function parsePrayer(
  rawText: string,
  relatedContext: string | undefined,
  existingPeople: string[] | undefined,
  authorization: ParsePrayerAuthorization,
): Promise<ParsedPrayer> {
  const ownerBinding = captureOutboundOwnerBinding();
  if (!rawText.trim()) {
    throw new ParseError('Prayer text is empty.');
  }
  if (
    authorization.target.requiresPublic ||
    authorization.context.some((expected) => !expected.requiresPublic) ||
    ((relatedContext || existingPeople?.length) && authorization.context.length === 0)
  ) {
    throw new ParseError('Prayer authorization is incomplete.');
  }

  const payload: {
    rawText: string;
    relatedContext?: string;
    existingPeople?: string[];
    language: string;
  } = {
    rawText: preparePrayerEnrichmentText(rawText),
    language: currentLang(),
  };
  if (relatedContext) payload.relatedContext = relatedContext;
  // Pass the user's existing canonical name list so Grok reuses exact
  // forms ("my mother" → "Mom" when Mom is already on the list). This
  // is the primary defense against duplicate people profiles — see
  // prayer-utils `existingPeopleList` and the parse-prompt rule.
  if (existingPeople && existingPeople.length > 0) {
    payload.existingPeople = existingPeople;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);

  let res: Response;
  try {
    const headers = await authorizePrayerRequest(ownerBinding, [
      authorization.target,
      ...authorization.context,
    ]);
    res = await fetch(PROXY_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeout);
    if ((err as Error).name === 'AbortError') {
      throw new ParseError('Parse timed out (30s).');
    }
    throw err;
  }
  clearTimeout(timeout);

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ParseError(body.error ?? `Proxy error (${res.status})`, res.status);
  }

  const data = await res.json();
  return normalizeParsedPrayerResponse(data);
}

export function normalizeParsedPrayerResponse(data: unknown): ParsedPrayer {
  if (!data || typeof data !== 'object') {
    throw new ParseError('Unexpected response from proxy.');
  }

  const record = data as Record<string, unknown>;

  const who = typeof record.who === 'string' ? record.who.trim() : '';
  const corePrayer =
    typeof record.core_prayer === 'string' ? record.core_prayer.trim() : '';
  if (!who || !corePrayer) {
    throw new ParseError('Incomplete response from proxy.');
  }

  const suggestedStatus =
    record.suggestedStatus === 'GodIsMoving' ? 'GodIsMoving' as const : null;

  // Defensive coercion: Grok may return a string array, a malformed value,
  // or omit the field entirely on older prompts. Normalize to string[] with
  // trim/dedupe/cap at 10 so downstream surfaces never see junk.
  const mentionedPeople = coerceMentionedPeople(record.mentionedPeople);

  // B2a.3 — cardLabel client-side coercion. Server (api/grok.ts) already
  // validated against CARD_NOUN_ALLOWLIST and narrowed unrecognized values
  // (plus any non-null on es/pt) to null. We just type-narrow here so the
  // wrapper's return literal carries the field through to ParsedPrayer —
  // without this include, the 4-field literal silently drops cardLabel
  // at the client boundary regardless of what the server sent.
  const cardLabel = typeof record.cardLabel === 'string' ? record.cardLabel : null;

  return {
    who,
    core_prayer: corePrayer,
    suggestedStatus,
    mentionedPeople,
    cardLabel,
  };
}

function coerceMentionedPeople(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const v of value) {
    if (typeof v !== 'string') continue;
    const name = v.trim();
    if (!name) continue;
    // Guard against Grok slipping in address words despite prompt rules.
    const lower = name.toLowerCase();
    if (lower === 'god' || lower === 'lord' || lower === 'jesus' || lower === 'christ' || lower === 'holy spirit') continue;
    const key = lower;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(name);
    if (result.length >= 10) break;
  }
  return result;
}

export interface SummaryResult {
  summary: string;
  recommendedStage: 'Still praying' | 'God is moving' | null;
}

export async function generateGardenSummary(prayer: Prayer): Promise<SummaryResult> {
  const ownerBinding = captureOutboundOwnerBinding();
  const thread = buildGardenSummaryThread(prayer);

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 20000);

  let res: Response;
  try {
    const headers = await authorizePrayerRequest(ownerBinding, [
      prayerOutboundExpectation(prayer, false),
    ]);
    res = await fetch(SUMMARY_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ thread, language: currentLang() }),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeout);
    if ((err as Error).name === 'AbortError') {
      throw new ParseError('Summary timed out.');
    }
    throw err;
  }
  clearTimeout(timeout);

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ParseError(body.error ?? `Summary error (${res.status})`, res.status);
  }

  const data = await res.json();
  const summary =
    data && typeof data.summary === 'string' ? data.summary.trim() : '';
  if (!summary) throw new ParseError('Incomplete summary response.');
  const stage = data.recommendedStage;
  return {
    summary,
    recommendedStage: stage === 'God is moving' ? 'God is moving' : stage === 'Still praying' ? 'Still praying' : null,
  };
}

export interface ThemeCluster {
  label: string;
  prayerIds: string[];
  span: string;
}

export type ThemeInputPrayer = PrayerThemeInput;

/**
 * On-demand clustering of a prayer subset into factual themes. Used by
 * Person profile and (later) the Remember view + keepsake assembly.
 * Fire-and-forget: caller shows the UI first and swaps in clusters
 * when they arrive. The caller pre-filters held-close prayers, then this
 * boundary atomically rechecks every exact public revision and the durable
 * owner after auth resolves. Both layers are required by additive privacy.
 * Empty input (<2 prayers) short-circuits server-side with no API call.
 */
const themeClusterCache = new Map<string, Promise<ThemeCluster[]>>();
const MAX_THEME_CACHE_ENTRIES = 50;

/** One identity for authorization, cache and the rendered result. */
export function themeInputKey(
  prayers: readonly ThemeInputPrayer[],
  language = currentLang(),
  binding = captureOutboundOwnerBinding(),
): string {
  return cj1([binding?.ownerId ?? null, binding?.authEpoch ?? null, language,
    prayers.map((prayer) => [prayer.id, prayer.updatedAt, prayer.createdAt, prayer.text,
      prayer.summary ?? null, prayer.testimony ?? null])]);
}

export async function generateThemeClusters(
  prayers: ThemeInputPrayer[],
  language = currentLang(),
): Promise<ThemeCluster[]> {
  const ownerBinding = captureOutboundOwnerBinding();
  if (prayers.length < 2) return [];
  // Copy before awaiting auth so every boundary uses the identical input.
  const input = prayers.map((prayer) => ({ ...prayer }));
  const expectations = input.map(themeOutboundExpectation);
  let headers: Record<string, string>;
  try {
    headers = await authorizePrayerRequest(ownerBinding, expectations);
  } catch {
    return [];
  }
  const key = themeInputKey(input, language, ownerBinding);
  const hit = themeClusterCache.get(key);
  if (hit) return hit;
  const request = requestThemeClusters(input, headers, language).catch((error) => {
    themeClusterCache.delete(key);
    throw error;
  });
  themeClusterCache.set(key, request);
  if (themeClusterCache.size > MAX_THEME_CACHE_ENTRIES) {
    const oldest = themeClusterCache.keys().next().value;
    if (typeof oldest === 'string') themeClusterCache.delete(oldest);
  }
  return request;
}

async function requestThemeClusters(
  prayers: ThemeInputPrayer[],
  headers: Record<string, string>,
  language: string,
): Promise<ThemeCluster[]> {

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 25000);

  let res: Response;
  try {
    res = await fetch(THEMES_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({ prayers, language }),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeout);
    if ((err as Error).name === 'AbortError') {
      throw new ParseError('Themes timed out.');
    }
    throw err;
  }
  clearTimeout(timeout);

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ParseError(body.error ?? `Themes error (${res.status})`, res.status);
  }

  const data = await res.json();
  const raw = Array.isArray(data?.themes) ? data.themes : [];
  // Defensive coercion: validate every field, keep only well-formed
  // clusters, cap at 6 (matches the prompt contract; belt-and-braces
  // in case Grok over-generates).
  const result: ThemeCluster[] = [];
  for (const v of raw) {
    if (!v || typeof v !== 'object') continue;
    const rec = v as Record<string, unknown>;
    const label = typeof rec.label === 'string' ? rec.label.trim() : '';
    const span = typeof rec.span === 'string' ? rec.span.trim() : '';
    const ids = Array.isArray(rec.prayerIds)
      ? rec.prayerIds.filter((x): x is string => typeof x === 'string')
      : [];
    if (!label || ids.length < 2) continue;
    result.push({ label, prayerIds: ids, span });
    if (result.length >= 6) break;
  }
  return result;
}

/**
 * Match the filename extension to the actual audio container so xAI's
 * STT decoder picks the right codec. Sending Safari mp4 bytes as
 * 'prayer.webm' was causing empty transcriptions on iOS/Safari.
 *
 * Exported because V.A.2 needs the same mapping client-side when
 * constructing storage paths (`${userId}/${queueId}.${ext}`) — the v2
 * endpoint derives its filename from the path's suffix, so the path's
 * extension MUST match the actual container. audio-queue.ts's
 * uploadAudioToStorage helper consumes this.
 */
export function extFromMimeType(type: string): string {
  if (!type) return 'webm';
  const t = type.toLowerCase();
  if (t.includes('mp4')) return 'm4a';
  if (t.includes('mpeg')) return 'mp3';
  if (t.includes('ogg')) return 'ogg';
  if (t.includes('wav')) return 'wav';
  return 'webm';
}

/**
 * V.A.2: transcribe via the Node-runtime endpoint that reads bytes
 * from Supabase Storage. Caller uploads the audio to Storage first
 * (via `uploadAudioToStorage` in audio-queue.ts) and passes only the
 * registry queue identity plus its compare-and-swap revision. Body is JSON,
 * not multipart — the
 * Vercel function's 4.5 MB request body cap doesn't apply because
 * the audio bytes never traverse the function's request path.
 *
 * Auth is mandatory on the v2 endpoint (rule #31 — Node-runtime
 * route, server-validated via supabase admin client). The server derives the
 * exact private Storage path from the owner-scoped registry; callers cannot
 * nominate an arbitrary object path.
 *
 * Retry posture is preserved verbatim from the pre-V.A.2 path —
 * V.A.1's endpoint contract returns the same status codes:
 *   502 / 503 / 5xx other → retry-fast  (400, 800 ms inline backoff)
 *   504 / 408             → retry-slow  (800, 1600 ms)
 *   413 / 4xx other       → terminal    (no retry — same path will
 *                                        fail identically)
 *   429 / 401             → no inline retry, queue retry OK
 * Max 3 total inline attempts; loop returns on success or throws.
 */
export interface RegisteredAudioTranscription {
  text: string;
  registryRevision: string;
  registryState: string;
}

export async function transcribeAudio(input: {
  queueId: string;
  expectedRegistryRevision: string;
  /**
   * A live capture's continuation check (voice-capture custody). Asked before
   * every attempt, so a retired capture starts no further request, including
   * an inline retry after a held 5xx. Refusal throws a plain Error, not a
   * ParseError: it is not a transcription outcome.
   */
  shouldContinue?: () => boolean;
}): Promise<RegisteredAudioTranscription> {
  if (!enhancedVoiceAllowed()) {
    throw new ParseError('Enhanced voice is disabled.', 503);
  }
  const timing = createVoiceTiming('transcribe');
  try {
    const queueId = input.queueId?.trim();
    const expectedRegistryRevision = input.expectedRegistryRevision?.trim();
    if (!queueId || !expectedRegistryRevision) {
      throw new ParseError('Missing registered audio identity.');
    }

    // Auth is read once per call — session token is stable within the
    // retry window. The v2 endpoint requires a Bearer token; the
    // server's path-traversal check rejects requests whose userId
    // doesn't match the path's leading folder.
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    timing?.start('auth');
    const sb = getSupabase();
    if (sb) {
      const { data } = await sb.auth.getSession();
      if (data.session?.access_token) {
        headers['Authorization'] = `Bearer ${data.session.access_token}`;
      }
    }

    timing?.end('auth');
    const body = JSON.stringify({ queueId, expectedRegistryRevision });
    const MAX_ATTEMPTS = 3;
    let lastErr: ParseError | null = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      if (input.shouldContinue && !input.shouldContinue()) {
        throw new Error('audio-capture-retired');
      }
      const controller = new AbortController();
      // Bumped from 30s on the legacy Edge endpoint to 300s here because
      // v2 is Node-runtime with maxDuration 300s (vercel.json). Long
      // prayers (~10 min Opus) take 30-90s for xAI to process; the prior
      // 30s ceiling would have aborted long-prayer success cases.
      const timeout = setTimeout(() => controller.abort(), 300000);

      let res: Response;
      try {
        timing?.start('request');
        res = await fetch(TRANSCRIBE_URL, {
          method: 'POST',
          headers,
          body,
          signal: controller.signal,
        });
      } catch (err) {
        clearTimeout(timeout);
        if ((err as Error).name === 'AbortError') {
          const timedOut = new ParseError('Transcription timed out.');
          // The request's own timeout is the classifier's timeout class (408).
          timedOut.temporaryServiceFailure = !classifyTranscribeStatus(408).terminal;
          throw timedOut;
        }
        throw err;
      }
      clearTimeout(timeout);
      timing?.end('request');
      timing?.server(res);

      if (res.ok) {
        timing?.start('response_body');
        const data = await res.json();
        timing?.end('response_body');
        const text = String(data.text ?? '').trim();
        const registryRevision = String(data.registryRevision ?? '').trim();
        const registryState = String(data.registryState ?? '').trim();
        if (!registryRevision || !registryState) {
          const incomplete = new ParseError('Incomplete transcription receipt.', 502);
          incomplete.temporaryServiceFailure = !classifyTranscribeStatus(502).terminal;
          throw incomplete;
        }
        return { text, registryRevision, registryState };
      }

      timing?.start('response_body');
      const respBody = await res.json().catch(() => ({})) as Record<string, unknown>;
      timing?.end('response_body');
      const responseError = typeof respBody.error === 'string'
        ? respBody.error
        : `Transcription error (${res.status})`;
      const responseDetail = typeof respBody.detail === 'string'
        ? respBody.detail
        : '';
      const msg = responseDetail ? `${responseError}: ${responseDetail}` : responseError;
      const classification = classifyTranscribeStatus(res.status);
      lastErr = new ParseError(msg, res.status);
      if (typeof respBody.error === 'string' && respBody.error.trim()) {
        lastErr.serverCode = respBody.error.trim();
      }
      if (
        typeof respBody.registryRevision === 'string' &&
        /^[1-9][0-9]*$/.test(respBody.registryRevision)
      ) lastErr.registryRevision = respBody.registryRevision;
      if (
        typeof respBody.registryState === 'string' &&
        respBody.registryState.trim()
      ) lastErr.registryState = respBody.registryState.trim();
      const casConflict =
        lastErr.serverCode === 'revision_conflict' ||
        lastErr.serverCode === 'lease_conflict' ||
        lastErr.serverCode === 'state_conflict';
      if (classification.terminal && !casConflict) lastErr.terminal = true;
      // 429 and 401 are not terminal in the classifier (the queue may retry
      // once the limit or the token resets), so they count as temporary too.
      if (!classification.terminal && !casConflict) lastErr.temporaryServiceFailure = true;

      if (casConflict || !classification.inlineRetry || attempt === MAX_ATTEMPTS) {
        throw lastErr;
      }

      timing?.start('retry_wait');
      await new Promise((resolve) =>
        setTimeout(resolve, attempt * classification.backoffBaseMs),
      );
      timing?.end('retry_wait');
    }

    // Unreachable — the loop either returns on success or throws.
    throw lastErr ?? new ParseError('Transcription failed after retries.');
  } finally {
    timing?.finish();
  }
}

/**
 * Rule #21: whether a failure is a temporary failure of the transcription
 * service, the only kind that starts the voice hook's one-minute service
 * fallback. Decided by transcribeAudio from classifyTranscribeStatus (see
 * ParseError.temporaryServiceFailure); consumers never branch on a status.
 */
export function isTemporaryTranscriptionServiceFailure(error: unknown): boolean {
  return error instanceof ParseError && error.temporaryServiceFailure === true;
}

/**
 * V.B.1: classify an HTTP status code from /api/grok-transcribe into a
 * retry posture. See the comment block above the retry loop for the
 * full table; this helper is the source of truth.
 */
type TranscribeClassification = {
  inlineRetry: boolean;
  /** Base backoff in ms; multiplied by attempt number for linear growth. */
  backoffBaseMs: number;
  terminal: boolean;
};

function classifyTranscribeStatus(status: number): TranscribeClassification {
  if (status === 502 || status === 503) {
    return { inlineRetry: true, backoffBaseMs: 400, terminal: false };
  }
  if (status === 504 || status === 408) {
    return { inlineRetry: true, backoffBaseMs: 800, terminal: false };
  }
  if (status === 413) {
    return { inlineRetry: false, backoffBaseMs: 0, terminal: true };
  }
  if (status === 429 || status === 401) {
    return { inlineRetry: false, backoffBaseMs: 0, terminal: false };
  }
  if (status >= 400 && status < 500) {
    return { inlineRetry: false, backoffBaseMs: 0, terminal: true };
  }
  if (status >= 500) {
    return { inlineRetry: true, backoffBaseMs: 400, terminal: false };
  }
  return { inlineRetry: false, backoffBaseMs: 0, terminal: true };
}
